import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { getWorkQueue, getPendingApprovals, getItems } from '../utils/api';
import LifecyclePill from '../components/LifecyclePill';

export default function WorkQueue() {
  const { selectedProject } = useOutletContext();
  const [queue, setQueue] = useState(null);
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (selectedProject?.id) loadAll();
  }, [selectedProject]);

  async function loadAll() {
    setLoading(true);
    try {
      const [queueData, approvalsData] = await Promise.all([
        getWorkQueue(selectedProject.id),
        getPendingApprovals(),
      ]);
      setQueue(queueData || {});
      setApprovals(approvalsData || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  const packagesNeedingAction = queue?.packages_needing_action || [];
  const lowConfItems = queue?.low_confidence_items || 0;

  const ACTION_LABELS = {
    scope_review:         { label: 'Scope needs review', action: 'Review scope', color: 'var(--color-warning)' },
    rfq_drafted:          { label: 'RFQ draft ready for approval', action: 'Approve RFQ', color: 'var(--color-info)' },
    quotes_received:      { label: 'Quotes received — run bid leveling', action: 'Level bids', color: 'var(--color-warning)' },
    bid_leveling:         { label: 'Bid leveling in progress', action: 'Review bids', color: 'var(--color-warning)' },
    award_recommendation: { label: 'Award recommendation ready', action: 'Approve award', color: 'var(--color-success)' },
    contract_po_pending:  { label: 'Contract/PO pending', action: 'Issue contract', color: 'var(--color-teal)' },
    invoice_pending:      { label: 'Invoice pending review', action: 'Review invoice', color: 'var(--color-warning)' },
  };

  if (loading && selectedProject?.id) return <div className="page"><div className="empty-state"><div className="spinner" /></div></div>;

  if (!selectedProject?.id) return (
    <div className="page">
      <div className="empty-state" style={{ minHeight: 400 }}>
        <div className="empty-state-icon">✦</div>
        <h3>No project selected</h3>
        <p>Create a project first to see the work queue</p>
        <button className="btn btn-primary" onClick={() => navigate('/control')}>Go to Project Control</button>
      </div>
    </div>
  );

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">AI Work Queue</div>
          <div className="page-subtitle">Everything AI has prepared — waiting for your review or approval</div>
        </div>
        <div className="page-actions">
          <button onClick={loadAll} className="btn btn-secondary btn-sm">↻ Refresh</button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid-3" style={{ marginBottom: 20 }}>
        {[
          { label: 'Packages Needing Action', value: packagesNeedingAction.length, color: 'var(--color-warning)' },
          { label: 'Pending Approvals', value: approvals.length, color: 'var(--color-info)' },
          { label: 'Low Confidence Items', value: lowConfItems, color: 'var(--color-error)' },
        ].map(item => (
          <div key={item.label} className="kpi-card" style={{ borderLeft: `3px solid ${item.color}` }}>
            <div className="kpi-card-label">{item.label}</div>
            <div className="kpi-card-value" style={{ color: item.value > 0 ? item.color : 'var(--color-text-dark)' }}>{item.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

        {/* Packages needing action */}
        {packagesNeedingAction.length > 0 && (
          <div className="card">
            <div className="card-header">
              <span style={{ fontWeight: 600 }}>Packages Needing Action</span>
              <span className="badge badge-warning">{packagesNeedingAction.length}</span>
            </div>
            <div>
              {packagesNeedingAction.map((pkg, i) => {
                const meta = ACTION_LABELS[pkg.lifecycle_state] || { label: 'Needs attention', action: 'Review', color: 'var(--color-text-light)' };
                return (
                  <div key={pkg.id} style={{
                    display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px',
                    borderBottom: i < packagesNeedingAction.length - 1 ? '1px solid var(--color-border)' : 'none',
                    borderLeft: `3px solid ${meta.color}`,
                  }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 4 }}>{pkg.name}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <LifecyclePill state={pkg.lifecycle_state} />
                        <span style={{ fontSize: '0.75rem', color: meta.color, fontWeight: 500 }}>{meta.label}</span>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      <button onClick={() => navigate(`/packages/${pkg.id}`)} className="btn btn-primary btn-sm">
                        {meta.action} →
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Pending approvals */}
        {approvals.length > 0 && (
          <div className="card">
            <div className="card-header">
              <span style={{ fontWeight: 600 }}>Pending Approvals</span>
              <span className="badge badge-info">{approvals.length}</span>
            </div>
            <div>
              {approvals.map((approval, i) => (
                <div key={approval.id} style={{
                  display: 'flex', alignItems: 'center', gap: 16, padding: '14px 20px',
                  borderBottom: i < approvals.length - 1 ? '1px solid var(--color-border)' : 'none',
                  borderLeft: '3px solid var(--color-info)',
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 2 }}>
                      {approval.approval_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-light)' }}>
                      {approval.entity_type} #{approval.entity_id}
                      {approval.requester_name && ` · Requested by ${approval.requester_name}`}
                    </div>
                  </div>
                  <button onClick={() => navigate(`/packages/${approval.entity_id}`)} className="btn btn-secondary btn-sm">
                    Review →
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Low confidence items */}
        {lowConfItems > 0 && (
          <div className="card">
            <div className="card-header">
              <span style={{ fontWeight: 600 }}>Low Confidence Extractions</span>
              <span className="badge badge-error">{lowConfItems}</span>
            </div>
            <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontSize: '0.875rem', fontWeight: 500, marginBottom: 4 }}>
                  {lowConfItems} extracted item{lowConfItems !== 1 ? 's' : ''} need review
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-mid)' }}>
                  AI confidence below 70% — human review recommended before assigning to packages
                </div>
              </div>
              <button onClick={() => navigate('/extract')} className="btn btn-primary btn-sm">
                Review Items →
              </button>
            </div>
          </div>
        )}

        {/* All clear state */}
        {packagesNeedingAction.length === 0 && approvals.length === 0 && lowConfItems === 0 && (
          <div className="empty-state card" style={{ padding: 48 }}>
            <div style={{ fontSize: '2rem' }}>✓</div>
            <h3>All clear</h3>
            <p>No packages need attention right now. AI is ready to work when you upload documents or create packages.</p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => navigate('/documents')} className="btn btn-primary">Upload Documents</button>
              <button onClick={() => navigate('/packages')} className="btn btn-secondary">Create Package</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}